import type { StyleProps } from '@chakra-ui/react';

/**
 * CONENG-2667 — MSAI/MBA Hero Takeover. The hero variant (G1/G2) is selected by the CF Worker flag
 * (`masters_lp_hero_takeover` → `g1`/`g2` variationKey), which serves the matching flag page under
 * `src/pages/flag/masters_lp_hero_takeover/{g1,g2}/...`. This module holds the per-degree, per-variant
 * content + checkout hrefs for that hero.
 *
 * The flag key is intentionally generic (not tied to a specific sale) so the takeover can be turned
 * back on for future campaigns by re-pointing the CF Worker variationKey and updating the copy /
 * coupons below. The current run is the Labor Day 2026 sale.
 *
 * G1 (`sept50`) — full-price markets, 50% off; coupon `SEPT50`.
 * G2 (`sept10`) — low-price markets, 10% off; coupon `SEPT10`.
 *
 * The sale badge (gold rings + curved "LABOR DAY SALE" / "SITEWIDE SALE" text) is rendered in code
 * by `HeroTakeoverBadge` so the text can be driven by the variant; the per-degree solid base color
 * (MSAI navy / MBA crimson) is applied via CSS so a single badge serves both degrees.
 */
export type HeroTakeoverVariant = 'g1' | 'g2';

export type HeroTakeoverFormalDegreeKey = 'ms_ai' | 'mba_ai';

export const HERO_TAKEOVER_COUPON_CODES: Record<HeroTakeoverVariant, string> = {
  g1: 'SEPT50',
  g2: 'SEPT10',
};

export const HERO_TAKEOVER_SUBSCRIBER_CHECKOUT_PATH = '/checkout/individual/monthly/credit-card';

export const HERO_TAKEOVER_DEGREE_APPLICATION_CHECKOUT_PATH = '/checkout/degree-application/purchase/credit-card';

export const HERO_TAKEOVER_CTA_LABEL = 'Enroll Now';

export const HERO_TAKEOVER_STRIKETHROUGH_IMAGE = '/images/promotions/msai-mba-hero/strikethrough.png';

/**
 * Width of the right-hand art column that carries the sale badge, as a share of the
 * (max 1440px) hero background box, from `md` up.
 *
 * The badge occupies 47.5%–86.8% of the source artwork's width, so with `object-fit: contain`
 * anchored right its left edge sits at `columnRight - 0.525 × width` and its diameter is
 * `0.393 × width`. Sizing the column as a percentage is what makes the badge shrink on narrow
 * viewports instead of holding the fixed, hero-height-derived size that `cover` gave it — which is
 * what used to drive it into the headline between 768px and ~1150px. At 57% the badge clears the
 * text column at every breakpoint, and from 1440px up the hero's height rather than the column
 * limits the artwork, so the composition matches the original design exactly.
 */
export const HERO_TAKEOVER_DESKTOP_ART_WIDTH = '57%';

/**
 * Enroll CTA target. The paths mirror `AccreditationHeroSection` so the promo hero sells the same
 * product as the default hero: active subscribers add the degree application to their existing
 * subscription, everyone else buys the standalone degree application.
 *
 * The variant coupon rides along as `?coupon=`, which the CF Worker (`getCouponCookie`) turns into
 * the `COUPON_COOKIE` that checkout actually reads. Falls back to the fixed sale coupon
 * (`SEPT50` / `SEPT10`) when no live `promoCode` is available yet.
 */
export function getHeroTakeoverCheckoutHref({
  formalDegreeKey,
  hasActiveSubscription,
  promoCode,
  variant,
}: {
  formalDegreeKey: HeroTakeoverFormalDegreeKey;
  hasActiveSubscription?: boolean | null;
  promoCode?: string | null;
  variant: HeroTakeoverVariant;
}): string {
  const params = new URLSearchParams();
  if (hasActiveSubscription) {
    params.set('degreeApplication', 'true');
  }
  params.set('formalDegreeKey', formalDegreeKey);
  params.set('coupon', promoCode ?? HERO_TAKEOVER_COUPON_CODES[variant]);

  const path = hasActiveSubscription
    ? HERO_TAKEOVER_SUBSCRIBER_CHECKOUT_PATH
    : HERO_TAKEOVER_DEGREE_APPLICATION_CHECKOUT_PATH;

  return `${path}?${params.toString()}`;
}

export type HeroTakeoverHeadline = {
  /** Gold eyebrow prefix, e.g. "Sitewide sale: ". */
  eyebrow: string;
  /** White headline body before the price line, e.g. "Earn a Master's in AI for ". */
  prefix: string;
  /** Copy glued to the price so it never wraps away from it, e.g. "under ". */
  priceLabel: string;
  /** Strikethrough original price for G1, e.g. "$5K". Omitted for G2 (no strikethrough). */
  originalPrice?: string;
  /** White sale price suffix, e.g. " $2.5K USD" (G1) or "$1.6K USD" (G2). */
  salePrice: string;
  /**
   * Start `priceLabel` + price on their own headline line instead of letting the headline wrap
   * naturally. MBA only, per PM feedback on the CONENG-2593 hero.
   */
  priceOnOwnLine?: boolean;
};

type HeroTakeoverPageContent = {
  /** Solid base color behind the transparent background art (full-viewport section background). */
  backgroundColor: string;
  /**
   * Full-viewport legibility gradient layered over `backgroundColor` (Figma main-content gradient):
   * `linear-gradient(90deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)`. Renders as a near-black left edge
   * fading to the base color on the right. Applied to the outer HeroLayout section so it spans the
   * viewport width, not just the centered 1440px image box.
   */
  viewportBackgroundGradient: StyleProps['bgGradient'];
  subtitle: string;
  headlines: Record<HeroTakeoverVariant, HeroTakeoverHeadline>;
};

export const HERO_TAKEOVER_PAGE_CONTENT: Record<HeroTakeoverFormalDegreeKey, HeroTakeoverPageContent> = {
  ms_ai: {
    backgroundColor: '#031643',
    viewportBackgroundGradient: 'linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%)',
    subtitle: 'Save for a limited time on a flexible, project-based AI degree built for career growth.',
    headlines: {
      g1: {
        eyebrow: 'Sitewide sale: ',
        prefix: "Earn a Master's in AI for ",
        priceLabel: 'under ',
        originalPrice: '$5K',
        salePrice: ' $2.5K USD',
      },
      g2: {
        eyebrow: 'Sitewide sale: ',
        prefix: "Earn a Master's in AI for ",
        priceLabel: 'under ',
        salePrice: '$1.6K USD',
      },
    },
  },
  mba_ai: {
    backgroundColor: '#540916',
    viewportBackgroundGradient: 'linear-gradient(90deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%)',
    subtitle:
      'Save for a limited time on an accredited MBA that blends business fundamentals with AI product leadership and hands-on projects.',
    headlines: {
      g1: {
        eyebrow: 'Sitewide sale: ',
        prefix: 'Earn an MBA for ',
        priceLabel: 'under ',
        originalPrice: '$5K',
        salePrice: ' $2.5K USD',
        priceOnOwnLine: true,
      },
      g2: {
        eyebrow: 'Sitewide sale: ',
        prefix: 'Earn an MBA for ',
        priceLabel: 'under ',
        salePrice: '$1.6K USD',
        priceOnOwnLine: true,
      },
    },
  },
};
